import React, { Component } from 'react'

import { connect } from 'react-redux'

import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import Button from 'components/button/button'
import SectionControls from 'components/controls/section-controls'

import Milestone from '../milestone/milestone'

import AlertBanner from 'components/alerts/alert-banner'
import Onboarder from 'components/onboarder/onboarder'

import MobileFooterButtons from 'components/mobile/footer-buttons'

import Scroll from 'react-scroll'

import { forIn, throttle } from 'lodash'
import copy from 'fast-copy'
import isEqual from 'react-fast-compare'

import {
	openModal,
	updateMilestoneOrder,
	addOrRemoveSubscriber,
	getArchivedData,
	registerHistory,
	toggleSeeComplete,
	setOnboardingStep
} from 'state/actions'

import {
	getOffset,
	isMobile,
	getScreenSize,
	getUrlParams,
	getPrimaryColumnValue
} from 'utils'

import {
	getCurrentAccount,
	getUserRoles
} from 'state/selectors'

class ProjectTasksList extends Component {
	scroll = Scroll.animateScroll

	state = {
		searchTerm: '',
		areMilestonesReordering: false,
		areAllTasksCollapsed: false,
		currentOpenColumnId: null,
		currentFocusedTask: null,
		currentEditedTask: null,
		areTasksTransitionedIn: false,
		isFooterButtonsForcedShut: false,
		mobileSpacerHeight: 0,
		visibleIds: [],
	}

	menuState = {} // subset of state, for children
	taskActivityHistory = []
	taskOffsets = {}
	scrollCheckInterval = null // we'll do handleScroll every 1.5 seconds, just in case it misses any
	idToScrollTo = null

	clearTaskActivityHistory = _ => {
		this.taskActivityHistory = []
	}

	componentWillMount = _ => {
		window.addEventListener('scroll', throttle(this.handleScroll, 10))

		if (!isMobile()) {
			this.scrollCheckInterval = setInterval(_ => {
				this.handleScroll()
			}, 2000)
		}
	}

	componentWillUnmount = _ => {
		window.removeEventListener('scroll', throttle(this.handleScroll, 10))

		clearInterval(this.scrollCheckInterval)
	}

	componentDidMount = _ => {
		// So, this breaks the transition, for whatever reason.
		setTimeout(_ => {
			this.handleScroll()
		}, 1)

		this.assessMilestoneQuery()
	}

	componentWillUpdate = (nextProps, nextState) => {
		if (nextProps.keyboardHeight > this.props.keyboardHeight || (!this.state.currentFocusedTask && nextState.currentFocusedTask)) {
			if (!this.state.isFooterButtonsForcedShut) {
				this.setState({
					isFooterButtonsForcedShut: true
				})
			}
		} else {
			if (this.state.isFooterButtonsForcedShut) {
				this.setState({
					isFooterButtonsForcedShut: false
				})
			}
		}

		if (this.props.currentProject.id != nextProps.currentProject.id) {
			this.taskOffsets = {}
		}
	}

	componentDidUpdate = prevProps => {
		const {
			currentProject,
			milestonesArray,
			loading
		} = this.props

		if (!currentProject.milestones || !prevProps.currentProject.milestones || loading || prevProps.loading) {
			return
		}

		const milestonesCount = currentProject.milestones.length
		const prevMilestonesCount = prevProps.currentProject.milestones.length

		if (milestonesCount > prevMilestonesCount) {
			if (currentProject.id == prevProps.currentProject.id) {
				this.idToScrollTo = currentProject.milestones[(currentProject.milestones.length - 1)]
			}
		} else {
			this.assessMilestoneQuery(prevProps)
		}

		if (currentProject.id != prevProps.currentProject.id) {
			setTimeout(_ => {
				this.handleScroll()
			}, 1)
		}

		if (!isEqual(milestonesArray, prevProps.milestonesArray)) {
			this.recalibrateOffsets()
		}
	}

	shouldComponentUpdate = (nextProps, nextState) => {
		if (this.props.isMini) {
			return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState)
		}

		return true
	}

	handleScroll = _ => {
		if (isMobile()) {
			return
		}

		const visibleIds = []

		const scrollTop = window.pageYOffset

		const screenBottom = scrollTop + getScreenSize().height

		forIn(this.taskOffsets, (taskOffset, taskId) => {
			let isVisible = taskOffset.offset > (scrollTop - 200) && taskOffset.offset < (screenBottom + 200)

			// Just after rendering, tasks don't have an offset yet. There's a timing issue somewhere.
			if (!taskOffset.offset) {
				isVisible = true
			}

			if (isVisible) {
				if (taskId && !isNaN(parseInt(taskId))) {
					visibleIds.push(parseInt(taskId))
				}
			}
		})

		visibleIds.sort((a, b) => {
			return a - b
		})

		if (!isEqual(visibleIds, this.state.visibleIds)) {
			this.setState({
				visibleIds
			})
		}

		this.blockHandleScroll = false
	}

	assessMilestoneQuery = (prevProps = null) => {
		const milestoneId = getUrlParams('milestone')

		if (milestoneId) {
			window.location.href = window.location.href.replace('milestone=' + milestoneId, '')

			this.idToScrollTo = parseInt(milestoneId)
		}
	}

	blockHandleScroll = false
	taskRefsList = {}
	milestoneRefsList = {}
	shouldRecalcTimeout = null

	addTaskRef = (task, ref) => {
		if (isMobile()) {
			return
		}

		this.taskRefsList[task.id] = ref

		// So it'll wait a teeny bit (in case another task is calling this function), then recalibrate if it hasn't been interrupted.
		clearTimeout(this.shouldRecalcTimeout)

		this.shouldRecalcTimeout = setTimeout(_ => {
			this.recalibrateOffsets()
		}, 100)
	}

	addMilestoneRef = (milestone, ref) => {
		this.milestoneRefsList[milestone.id] = ref

		clearTimeout(this.shouldRecalcTimeout)

		this.shouldRecalcTimeout = setTimeout(_ => {
			this.recalibrateOffsets()
		}, 100)
	}

	recalibrateOffsets = _ => {
		if (isMobile()) {
			return
		}

		window.requestAnimationFrame(_ => {
			forIn(this.taskRefsList, (taskRef, taskId) => {
				const offsets = getOffset(taskRef)

				this.taskOffsets[taskId] = {
					offset: offsets.top,
					height: offsets.bounding.height
				}
			})

			forIn(this.milestoneRefsList, (milestoneRef, milestoneId) => {
				if (this.idToScrollTo) {
					if (milestoneId == this.idToScrollTo) {
						// subtracting 100 just for header(s)
						const offset = (getOffset(milestoneRef).top - 100)

						this.scroll.scrollTo(offset, {
							duration: 300,
							delay: 100,
							smooth: "easeInOutQuint",
						})

						this.idToScrollTo = null
					}
				}
			})

			this.handleScroll()
		})
	}

	forceAddVisibleId = task => {
		if (isMobile() || !task || !task.id) {
			return
		}

		const visibleIds = copy(this.state.visibleIds)
		visibleIds.push(task.id)

		this.taskOffsets[task.id] = {
			offset: 0,
			height: 0
		}

		this.setState({
			visibleIds
		})
	}

	setSpacerHeight = height => {
		this.setState({
			mobileSpacerHeight: height
		})
	}

	createMilestone = _ => {
		this.props.openModal('create', 'milestone', {
			milestone: null,
			project_id: this.props.currentProject.id
		})
	}

	customiseColumns = _ => {
		const {
			openModal,
			currentProject,
			userData,
			setOnboardingStep
		} = this.props

		setOnboardingStep('CUSTOMISED_PROJECT', userData)

		openModal('customise', 'project', {
			...currentProject
		})
	}

	onSearchTermChange = searchTerm => {
		this.setState({
			searchTerm
		})

		this.recalibrateOffsets()
	}

	deleteMilestone = milestone => {
		this.props.openModal('delete', 'milestone', {
			data: milestone,
			isShowingArchived: this.props.isShowingArchived
		})
	}

	restoreMilestone = milestone => {
		// we only need to restore its tasks, the milestone comes along automatically.
		this.props.openModal('restore', 'milestone', {
			data: milestone,
		})
	}

	deleteAllArchived = _ => {
		const {
			milestonesArray,
			allTasks,
			openModal
		} = this.props

		const allArchivedTasks = []

		milestonesArray.forEach(milestone => {
			milestone.archivedTasks.forEach(archivedTaskId => {
				const taskObject = allTasks[archivedTaskId]

				if (taskObject) {
					allArchivedTasks.push(taskObject)
				}
			})
		})

		openModal('delete', 'archived', { data: allArchivedTasks });
	}

	openMenu = (type, id, focusOnly = false) => {
		this.setState({
			currentOpenColumnId: !focusOnly ? type : null,
			currentFocusedTask: id,
		})
	}

	forceSetCurrentFocusedTask = id => {
		if (this.state.currentFocusedTask != id) {
			this.setState({
				currentFocusedTask: id
			})
		}
	}

	editTaskName = id => {
		this.setState({
			currentEditedTask: id
		})
	}

	addOrRemoveSubscriber = (user, account) => {
		this.props.addOrRemoveSubscriber(user, account)
	}

	selectedCheck = user => {
		let isSelected = false

		if (this.props.currentProject.subscriberIds.indexOf(user.id) != -1) {
			isSelected = true
		}

		return isSelected
	}

	selectCallback = user => {
		this.addOrRemoveSubscriber(user, this.props.currentProject)
	}

	toggleGantts = _ => {
		const {
			openModal,
			toggleGantts,
			isPremium
		} = this.props

		if (isPremium) {
			toggleGantts()
		} else {
			openModal('pricing', 'pricing', {
				feature: 'gantts'
			})
		}
	}

	getControlProps = _ => {
		const {
			currentProject,
			applicableUsers,
			isArchivedProject
		} = this.props

		let buttonProps = {}
		let checkboxProps = {}

		const defaultButtonProps = {
			size: 'big',
			disabled: isArchivedProject
		}

		buttonProps.deleteAll = {
			...defaultButtonProps,
			level: 'x-low',
			label: 'Delete all',
			icon: 'fe-trash-2',
			callback: this.deleteAllArchived,
		}

		buttonProps.createMilestone = {
			...defaultButtonProps,
			level: 'high',
			label: 'Create milestone',
			icon: 'plus',
			callback: this.createMilestone
		}

		buttonProps.customise = {
			...defaultButtonProps,
			level: 'low',
			label: `Customize`,
			icon: `fe-settings`,
			callback: this.customiseColumns,
			extra: (() => {
				const { roles } = this.props

				return !roles.guest
			})() ? (
				<Onboarder
					type='CUSTOMISED_PROJECT'
					direction="top-right"
					project={currentProject}
				/>
			) : null
		}

		buttonProps.subscribers = {
			...defaultButtonProps,
			level: 'low',
			label: currentProject.subscriberIds.length ? 'Subscribers (' + currentProject.subscriberIds.length + ')' : 'Subscribers',
			icon: 'fe-user-plus',
			dropDown: {
				type: 'user',
				items: applicableUsers,
				selectedCheck: this.selectedCheck,
				selectCallback: this.selectCallback
			}
		}

		buttonProps.swapView = {
			size: 'big',
			level: 'low',
			label: 'Gantt Charts',
			callback: this.toggleGantts,
			imgIcon: 'gantts'
		}

		buttonProps.sorting = {
			size: 'big',
			level: 'low',
			label: 'Sorting',
			icon: 'sort',
			dropDown: {
				type: 'sorting',
				items: [
					{
						label: 'Custom',
						value: 0
					}, {
						label: 'Mine first',
						value: 1
					}, {
						label: 'Alphabetical',
						value: 2
					}, {
						label: 'Status',
						value: 4
					}, {
						label: 'Due date',
						value: 3
					}
				],
				selectedCheck: item => {
					return false
				},
				selectCallback: item => {

				}
			}
		}

		return {
			buttons: buttonProps,
			checkboxes: checkboxProps
		}
	}

	toggleMilestonesReordering = _ => {
		let isReordering = !this.state.areMilestonesReordering

		if (isReordering) {
			this.setState({
				areAllTasksCollapsed: true
			})

			setTimeout(_ => {
				this.setState({
					areMilestonesReordering: true
				}, _ => {
					window.requestAnimationFrame(_ => {
						this.recalibrateOffsets()
					})
				})
			}, 300)
		} else {
			this.setState({
				areMilestonesReordering: false
			}, _ => {
				this.setState({
					areAllTasksCollapsed: false
				}, _ => {
					setTimeout(_ => {
						this.recalibrateOffsets()
					}, 300)
				})
			})
		}
	}

	toggleShowArchived = _ => {
		this.setState({
			areMilestonesReordering: false,
			areAllTasksCollapsed: false,
		})

		this.props.toggleShowArchived()
	}

	toggleShowCompleted = _ => {
		const {
			toggleSeeComplete,
			currentProject
		} = this.props

		this.setState({
			areMilestonesReordering: false,
			areAllTasksCollapsed: false,
		})

		toggleSeeComplete(currentProject.id)

		this.recalibrateOffsets()
	}

	toggleCollapseMilestone = id => {
		this.props.toggleCollapseMilestone(this.props.userData, id)

		// sometimes it's not really predictable when the collapsed milestone will be done rendering.
		setTimeout(_ => {
			this.recalibrateOffsets()
		}, 500)

		setTimeout(_ => {
			this.recalibrateOffsets()
		}, 1000)
	}

	processMenuState = _ => {
		const tempMenuState = {
			currentOpenColumnId: this.state.currentOpenColumnId,
			currentFocusedTask: this.state.currentFocusedTask,
			currentEditedTask: this.state.currentEditedTask
		}

		if (!isEqual(this.menuState, tempMenuState)) {
			this.menuState = tempMenuState
		}
	}

	hasCompletedTasks = _ => {
		const {
			milestonesArray,
			currentProject
		} = this.props

		let hasCompletedTasks = false

		milestonesArray && milestonesArray.some(milestone => {
			milestone.tasks && milestone.tasks.some(task => {
				const statusId = getPrimaryColumnValue('status', task, currentProject)

				if (statusId == 3) {
					hasCompletedTasks = true
					return true
				}
			})

			if (hasCompletedTasks) {
				return true
			}
		})

		return hasCompletedTasks
	}

	isHiddenBySearch = milestone => {
		const {
			searchTerm
		} = this.state

		if (!searchTerm || !milestone.name) {
			return false
		}

		const lowercaseSearchTerm = searchTerm.toLowerCase()

		const matchingTask = milestone.tasks && milestone.tasks.find(task => {
			return task.name && task.name.toLowerCase().includes(lowercaseSearchTerm)
		})

		return !matchingTask
	}

	render() {
		const {
			toggleView,
			milestonesArray,
			isShowingArchived,
			onDragStart,
			onDragEnd,
			draggedItemType,
			draggedItemId,
			toggleState,
			isLoadingArchived,
			loading,
			handleInputFocus,
			isDragging,
			applicableUsers,
			userData,
			currentProject,
			keyboardHeight,
			isPremium,
			accountData,
			isArchivedProject,
			isMini,
			isHidingComplete,
			roles
		} = this.props

		const {
			searchTerm,
			areMilestonesReordering,
			areAllTasksCollapsed,
			currentFocusedTask,
			currentEditedTask,
			areTasksTransitionedIn,
			isFooterButtonsForcedShut,
			mobileSpacerHeight,
			visibleIds,
		} = this.state

		const spacerHeight = keyboardHeight ? (keyboardHeight - 120) : mobileSpacerHeight

		const allControlProps = this.getControlProps()

		let hasTasks = false  // for archived milestones

		this.processMenuState()

		const buttonProps = [
			allControlProps.buttons.createMilestone,
			allControlProps.buttons.customise
		]

		if (currentProject.has_gantts || !isPremium) {
			buttonProps.push(allControlProps.buttons.swapView)
		}

		if (isShowingArchived) {
			milestonesArray.forEach(milestone => {
				if (milestone.archivedTasks.length) {
					hasTasks = true
				}
			})

			if (hasTasks) {
				buttonProps.unshift(allControlProps.buttons.deleteAll)
			}
		}

		const toggleButtonProps = [
			{
				label: 'See archived',
				isChecked: isLoadingArchived ? !isShowingArchived : isShowingArchived,
				callback: this.toggleShowArchived,
				icon: 'archive'
			}
		]

		if (this.hasCompletedTasks()) {
			toggleButtonProps.unshift({
				label: 'Show completed',
				isChecked: !isHidingComplete,
				callback: this.toggleShowCompleted,
				icon: 'completed'
			})
		}

		if (!isArchivedProject) {
			toggleButtonProps.unshift({
				label: 'Reorder milestones',
				isChecked: (areMilestonesReordering || areAllTasksCollapsed),
				callback: this.toggleMilestonesReordering,
				icon: 'reorder'
			})
		}

		const milestonesListClassName = `milestones-list ${ isLoadingArchived ? 'loading' : ''}`

		// not currently doing anything with this.
		const projectTasksClassName = `project-tasks ${ areTasksTransitionedIn ? 'visible' : ''}`

		let incrementer = 0

		return (
			<div className={projectTasksClassName} id="project-tasks">
				{!isMini &&
					<SectionControls
						buttons={buttonProps}
						toggleButtonProps={toggleButtonProps}
						toggleState={toggleState}
						toggleChangeCallback={_ => { toggleView() }}
						searchTerm={searchTerm}
						onSearchTermChange={this.onSearchTermChange}
						toggleOptions={null}
						buttonShelfHidden={areMilestonesReordering}
					/>
				}
				{(!isMini && isMobile()) &&
					<MobileFooterButtons
						buttons={buttonProps}
						isForcedShut={isFooterButtonsForcedShut}
					/>
				}
				{
					(!milestonesArray.length && !isShowingArchived) ?
						<AlertBanner title="This project is empty." body="Create a milestone to get started!" />
						: null
				}
				<DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
					<Droppable droppableId={'tasks'} type="MILESTONE">
						{provided => (
							<div className={isLoadingArchived ? 'milestones-list-container loading' : 'milestones-list-container'}>
								{isShowingArchived ?
									<AlertBanner title="Archived tasks are deleted after 90 days." body="It's good to clean up from time to time." />
									: null}
								<ul ref={provided.innerRef} className={milestonesListClassName}>
									{milestonesArray.map((milestone, milestoneIndex) => {
										const draggedTaskId = draggedItemType == 'TASK' ? draggedItemId : null

										// if not the first, increment tasks (for transitions)
										if (milestoneIndex) {
											incrementer += milestonesArray[(milestoneIndex - 1)].tasks.length
										}

										let isOpen = userData.collapsed_milestones ? userData.collapsed_milestones.indexOf(milestone.id) === -1 : true

										if (areAllTasksCollapsed) {
											isOpen = false
										}

										const isLast = (milestoneIndex >= (milestonesArray.length - 1))

										// if they're meeting follow-ups but empty, don't show them on task lists.
										if (!isMini && milestone.meeting_id && !milestone.tasks.length && !milestone.taskCount) {
											return null
										}

										const isHiddenBySearch = this.isHiddenBySearch(milestone)

										if (isHiddenBySearch) {
											return null
										}

										return (
											<Milestone
												accountData={accountData}
												addTaskRef={this.addTaskRef}
												addMilestoneRef={this.addMilestoneRef}
												applicableUsers={applicableUsers}
												areMilestonesReordering={areMilestonesReordering}
												clearTaskActivityHistory={this.clearTaskActivityHistory}
												collapseCallback={this.toggleCollapseMilestone}
												currentEditedTask={currentEditedTask}
												currentFocusedTask={currentFocusedTask}
												currentProject={currentProject}
												deleteMilestone={this.deleteMilestone}
												draggedTaskId={draggedTaskId}
												editTaskName={this.editTaskName}
												forceAddVisibleId={this.forceAddVisibleId}
												forceSetCurrentFocusedTask={this.forceSetCurrentFocusedTask}
												handleInputFocus={handleInputFocus}
												isArchivedProject={isArchivedProject}
												isDragging={isDragging}
												isHidingComplete={isHidingComplete}
												isLast={isLast}
												isMini={isMini}
												isPremium={isPremium}
												isOpen={isOpen}
												isShowingArchived={isShowingArchived}
												key={milestone.id}
												loading={loading}
												menuState={this.menuState}
												mobileSpacerHeight={mobileSpacerHeight}
												milestone={milestone}
												milestoneIndex={milestoneIndex}
												openMenu={this.openMenu}
												project={currentProject}
												restoreMilestone={this.restoreMilestone}
												roles={roles}
												searchTerm={searchTerm}
												setSpacerHeight={this.setSpacerHeight}
												taskActivityHistory={this.taskActivityHistory}
												transitionDelayStartIndex={incrementer}
												userData={userData}
												visibleIds={visibleIds}
												zIndex={(milestonesArray.length - milestoneIndex)}
											/>
										)
									})}
								</ul>
							</div>
						)}
					</Droppable>
				</DragDropContext>
				{
					(!isShowingArchived && !isMini) ?
						<div className="project-tasks-footer clear">
							<Onboarder
								type='CREATED_MILESTONE_AND_TASK'
								direction="right"
								project={currentProject}
							/>
							<Button
								size={'big'}
								level={'high'}
								callback={this.createMilestone}
								icon="plus"
								label={'Create milestone'}
								disabled={isArchivedProject}
							/>
						</div>
						: null
				}
				<div className="keyboard-height-spacer" style={{ height: spacerHeight + 'px' }} />
			</div>
		)
	}
}

const mapStateToProps = (state, props) => {
	return {
		keyboardHeight: state.account.keyboardHeight,
		accountData: getCurrentAccount(state),
		allTasks: state.account.entities.tasks,
		isHidingComplete: state.auth.hiddenCompleteProjectIds.indexOf(props.currentProject.id) !== -1,
		roles: getUserRoles(state, props.userData),
	}
}

export default connect(mapStateToProps, {
	openModal,
	updateMilestoneOrder,
	addOrRemoveSubscriber,
	getArchivedData,
	registerHistory,
	toggleSeeComplete,
	setOnboardingStep
})(ProjectTasksList)


// WEBPACK FOOTER //
// ./src/sections/projects/project/project-tasks/list/project-tasks-list.jsx